import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Alert } from 'antd';
import { messageModel } from '../MessageModel';
import { ChatMessageItem } from './ChatMessageItem';
import { getSelfUserId } from '../MessageConfig';

interface ChatMessageListProps {
  roomId: number;
}

const STICK_TO_BOTTOM_THRESHOLD_PX = 8;

export const ChatMessageList: React.FC<ChatMessageListProps> = observer(
  ({ roomId }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const stickToBottomRef = useRef(true);
    const lastMessageCountRef = useRef(0);
    const lastRoomIdRef = useRef(roomId);
    const selfId = getSelfUserId() || '';
    const messages = messageModel.getVisibleMessages(roomId);

    const jumpToBottom = useCallback(() => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      el.scrollTop = el.scrollHeight - el.clientHeight;
    }, []);

    const scrollToBottomAfterLayout = useCallback(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          jumpToBottom();
        });
      });
    }, [jumpToBottom]);

    const updateStickToBottom = useCallback(() => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      const distanceFromBottom =
        el.scrollHeight - el.clientHeight - el.scrollTop;
      stickToBottomRef.current =
        distanceFromBottom <= STICK_TO_BOTTOM_THRESHOLD_PX;
    }, []);

    useEffect(() => {
      scrollToBottomAfterLayout();
    }, [roomId, scrollToBottomAfterLayout]);

    useLayoutEffect(() => {
      if (lastRoomIdRef.current !== roomId) {
        lastRoomIdRef.current = roomId;
        lastMessageCountRef.current = messages.length;
        stickToBottomRef.current = true;
        jumpToBottom();
        return;
      }
      const prevCount = lastMessageCountRef.current;
      const nextCount = messages.length;
      lastMessageCountRef.current = nextCount;
      if (nextCount > prevCount && stickToBottomRef.current) {
        jumpToBottom();
      }
    }, [roomId, messages.length, jumpToBottom]);

    return (
      <div
        ref={scrollRef}
        className="message-list-scroll"
        onScroll={updateStickToBottom}
      >
        <div className="message-list-inner">
          <Alert
            type="info"
            showIcon
            message={
              messageModel.hasMoreHistory
                ? '仅展示近30天消息，更早记录请在 APP 中查看'
                : '仅展示近30天消息，更多历史请在 APP 中查看'
            }
            className="message-history-tip"
          />
          {messages.map((msg) => (
            <ChatMessageItem
              key={msg.id}
              message={msg}
              isSelf={msg.fromUserId === selfId}
            />
          ))}
        </div>
      </div>
    );
  },
);
